import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import Login from './pages/Login';
import MuvekkilPortal from './pages/MuvekkilPortal';
import MuvekkilLoglari from './pages/MuvekkilLoglari';
import StylePreview from './pages/StylePreview';
import Dashboard from './pages/Dashboard';
import CaseFiles from './pages/CaseFiles';
import ArchiveRequests from './pages/ArchiveRequests';
import ArchivedFiles from './pages/ArchivedFiles'; // Add this import
import Users from './pages/Users';
import Profile from './pages/Profile';
import Calendar from './pages/Calendar';
import CaseFileDetail from './pages/CaseFileDetail';
import Tasks from './pages/Tasks';
import Navbar from './components/Layout/Navbar';
import Sidebar from './components/Layout/Sidebar';
import BottomNav from './components/Layout/BottomNav';
import CamDock from './components/Layout/CamDock';
import OnayPenceresi from './components/Common/OnayPenceresi';
import { YENI_TASARIM, onizlemeyiAyarla } from './utils/yeniTasarim';
import { hatayiKaydet } from './utils/hataKaydi';
import AppNotificationsBootstrap from './components/Layout/AppNotificationsBootstrap';
import GuncellemeUyarisi from './components/Common/GuncellemeUyarisi';
import FaceIdTeklifi from './components/Common/FaceIdTeklifi';
import { surumTakibiniBaslat } from './utils/surumTakibi';
import Referanslar from './pages/Referanslar';
import MatbuDilekceler from './pages/MatbuDilekceler';
import ReferansYonetim from './pages/ReferansYonetim';
import ReferansCaseFiles from './pages/ReferansCaseFiles';
import Masraflar from './pages/Masraflar';
import MasrafTalepleri from './pages/MasrafTalepleri';
import JobRequests from './pages/JobRequests';
import JobList from './pages/JobList';
import Kasa from './pages/Kasa';
import KisiselGiderlerSayfasi from './pages/KisiselGiderlerSayfasi';
import Ayarlar from './pages/Ayarlar';
import GelenPostalar from './pages/GelenPostalar';
import Muvekkiller from './pages/Muvekkiller';
import Vekaletler from './pages/Vekaletler';
import UserLoginLogs from './pages/UserLoginLogs';
import CaseFileLogs from './pages/CaseFileLogs';
import MasrafLogs from './pages/MasrafLogs';
import Makbuzlar from './pages/Makbuzlar';
import MakbuzHesapla from './pages/MakbuzHesapla';
import IbranameMuvafakatnameler from './pages/IbranameMuvafakatnameler';
import TazminatHesap from './pages/TazminatHesap';
import YasalFaizHesap from './pages/YasalFaizHesap';
import VekaletUcretiHesapla from './pages/VekaletUcretiHesapla';
import IscilikAlacaklariHesap from './pages/IscilikAlacaklariHesap';
import AracHasarHesapSayfasi from './pages/AracHasarHesap';
import { canAccessOzelDosyalar } from './utils/ozelDosyaAccess';
import BackgroundUploadPanel from './components/Common/BackgroundUploadPanel';
import KalpYagmuru from './components/Common/KalpYagmuru';
import { consumeNativePickerRestorePath, isNativeApp, persistAppPath } from './utils/nativePicker';
import { derinBaglantiyiBaslat } from './utils/derinBaglanti';
import './App.css';

// Takvim onizlemesi yalnizca gelistirme ortaminda yuklenir; uretim
// paketine ornek (uydurma) veri girmesin.
const TakvimOnizleme = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import('./pages/TakvimOnizleme'))
  : null;

const MuvekkilRoute = ({ children }) => {
  const token = localStorage.getItem("muvekkilToken");
  return token ? children : <Navigate to="/login" />;
};

const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return children;

  /* MUVEKKIL OTURUMU KORUNUYOR.
     Uygulama her acilista "/" adresinden basliyor ve buraya dusuyor.
     Onceden personel oturumu yoksa dogrudan giris ekranina gidiliyordu;
     telefonda duran muvekkil oturumu hic denenmiyordu. Sonuc: muvekkil
     uygulamayi her kapatip actiginda sifre soruluyordu ve bildirime
     dokununca uygulama giris ekraninda acildigi icin dosyaya
     gidilemiyordu. Oturum gecersizse portal zaten girise geri
     gonderiyor (401 -> cikis), dongu olusmuyor. */
  if (localStorage.getItem('muvekkilToken')) return <Navigate to="/muvekkil" replace />;

  return <Navigate to="/login" />;
};

const OzelDosyaRoute = ({ children }) => {
  const { user } = useAuth();
  if (!canAccessOzelDosyalar(user)) {
    return <Navigate to="/dashboard" />;
  }
  return children;
};

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, bilgi) {
    // Sayfa coktugunde hatanin metni sunucu kaydina (utils/hataKaydi.js).
    const bilesen = String(bilgi?.componentStack || '').split(String.fromCharCode(10)).map((x) => x.trim()).filter(Boolean).slice(0, 3).join(' < ');
    hatayiKaydet('sayfa', error, bilesen ? 'bilesen=' + bilesen.slice(0, 160) : '');
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, maxWidth: 640 }}>
          <h2 style={{ marginTop: 0 }}>Sayfa yüklenemedi</h2>
          <p style={{ color: '#b91c1c' }}>{String(this.state.error?.message || this.state.error)}</p>
          <button type="button" onClick={() => window.location.assign('/dashboard')}>
            Anasayfaya dön
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const KasaRoute = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.Id ?? user?.id;
  const canAccess = userId === 2 || userId === 8;

  if (!canAccess) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const NativeRouteGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const restored = useRef(false);

  useEffect(() => {
    persistAppPath(location.pathname, location.search);
  }, [location.pathname, location.search]);

  /* Ana ekran widget'ina dokunuldugunda uygulama "ozturkhukuk://ac/..."
     adresiyle aciliyor; burada o adres ilgili ekrana cevriliyor.
     Oturum korumalari yerinde kaldigi icin yetkisiz bir ekran acilmaz. */
  const derinBaslatildi = useRef(false);
  useEffect(() => {
    if (derinBaslatildi.current || !isNativeApp()) return;
    derinBaslatildi.current = true;
    derinBaglantiyiBaslat(navigate);
  }, [navigate]);

  useEffect(() => {
    if (restored.current || !isNativeApp()) return;
    const current = `${location.pathname}${location.search || ''}`;

    // 1) Dosya seçiciden dönüş — kaldığı sayfaya geri döner.
    const pending = consumeNativePickerRestorePath();
    if (pending && pending !== current && pending !== '/login') {
      restored.current = true;
      navigate(pending, { replace: true });
      return;
    }

    // 2) Uygulama arka plana atılıp geri gelince iOS, WebView'i yeniden
    //    yükleyebiliyor. Sayfa kök adresten açıldığı için kullanıcı giriş
    //    ekranına düşüyordu — oysa oturum jetonu hâlâ duruyor. Jeton
    //    geçerliyse kaldığı yere geri götürüyoruz.
    if (current === '/' || current === '/login') {
      const muvekkilJeton = localStorage.getItem('muvekkilToken');
      const personelJeton = localStorage.getItem('token');
      const sonYol = localStorage.getItem('lastAppPath');

      if (muvekkilJeton) {
        restored.current = true;
        navigate('/muvekkil', { replace: true });
        return;
      }
      if (personelJeton && sonYol && sonYol !== '/login') {
        restored.current = true;
        navigate(sonYol, { replace: true });
      }
    }
  }, [location.pathname, location.search, navigate]);

  return null;
};

const AppContentWrapper = ({ children }) => {
  const location = useLocation();
  const isCaseFileDetail =
    (location.pathname.includes('/case-files/') && location.pathname !== '/case-files') ||
    (location.pathname.includes('/ozel-dosyalar/') && location.pathname !== '/ozel-dosyalar');
  const isCalendar = location.pathname === '/calendar';

  /* SAYFA GECISI (yalnizca yeni tasarim; telefon / uygulama): sayfa degisince icerik hafifce
     asagidan yukselerek belirir. Bilesen yeniden kurulmuyor (key
     kullanilmiyor) -- yalnizca Web Animations API ile ayni kutu canlandiriliyor,
     boylece sayfalarin durumu kaybolmuyor. Ilk acilista ve masaustunde
     calismaz; hareket azaltma tercihine uyulur. */
  const kutuRef = useRef(null);
  const ilkMi = useRef(true);
  useEffect(() => {
    if (!YENI_TASARIM) return;
    if (ilkMi.current) { ilkMi.current = false; return; }
    const el = kutuRef.current;
    if (!el || typeof el.animate !== 'function') return;
    const telefon = window.matchMedia?.('(max-width: 768px)').matches
      || document.body.classList.contains('capacitor-native');
    const azHareket = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!telefon || azHareket) return;
    el.animate(
      [
        { opacity: 0, transform: 'translateY(14px) scale(0.985)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 340, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
  }, [location.pathname]);

  return (
    <div ref={kutuRef} className={`app-content ${isCaseFileDetail ? 'case-file-detail-page' : ''} ${isCalendar ? 'calendar-page-wrapper' : ''}`}>
      {children}
    </div>
  );
};

// Ana sayfada navbar, karşılama bölümünün altında (Dashboard içinde) render edilir.
// Diğer tüm sayfalarda navbar en üstte gösterilir.
const ConditionalNavbar = () => {
  const location = useLocation();
  const isDashboard = location.pathname === '/dashboard' || location.pathname === '/';
  if (isDashboard) return null;
  return <Navbar />;
};

function App() {
  /* Uygulama siteyi internetten yukluyor; bir kez acildiktan sonra
     yayinlanan duzeltmeler kendiliginden gelmiyordu ve kullanicinin
     uygulamayi tamamen kapatip acmasi gerekiyordu. Artik uygulama one
     geldiginde yeni surum var mi diye bakiliyor, varsa sayfa bir kez
     yeniden yukleniyor (bkz. utils/surumTakibi.js). */
  useEffect(() => { surumTakibiniBaslat(); }, []);

  return (
    <AuthProvider>
      <SidebarProvider>
        <Router>
          <div className="app">
            {/* Yeni tasarim onizlemesi: ustte kucuk serit (cikis) ve
                iPhone tarzi onay penceresi. Canli derlemede hic yok. */}
            {YENI_TASARIM && (
              <div className="onizleme-serit" role="status">
                Yeni tasarım önizlemesi
                <button type="button" onClick={() => onizlemeyiAyarla(false)}>Çık</button>
              </div>
            )}
            {YENI_TASARIM && <OnayPenceresi />}
            {/* Eski surum uyarisi: uygulamada, oturum acmis ve surumu eski
                kullaniciya bir kez gosterilir. Muvekkil portali dahil her
                sayfada calissin diye Routes disinda duruyor. */}
            <GuncellemeUyarisi />
            <FaceIdTeklifi />
            <Routes>
              <Route path="/login" element={<Login />} />
              {/* Muvekkil portali: personel oturumundan bagimsiz, kendi token kontrolu var */}
              <Route path="/muvekkil" element={<MuvekkilRoute><MuvekkilPortal /></MuvekkilRoute>} />
              {/* Stil önizleme — yalnızca geliştirme ortamında derlenir,
                  üretim derlemesinde bu route hiç oluşmaz. */}
              {process.env.NODE_ENV === 'development' && (
                <Route path="/style-preview" element={<StylePreview />} />
              )}
              {process.env.NODE_ENV === 'development' && (
                <Route path="/takvim-onizleme" element={<React.Suspense fallback={null}><TakvimOnizleme /></React.Suspense>} />
              )}
              <Route
                path="/*"
                element={
                  <PrivateRoute>
                    <>
                      <AppNotificationsBootstrap />
                      <NativeRouteGuard />
                      <Sidebar />
                      <ConditionalNavbar />
                      <AppContentWrapper>
                      <Routes>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/case-files" element={<CaseFiles />} />
                        <Route path="/case-files/:id" element={<CaseFileDetail />} />
                        <Route path="/ozel-dosyalar" element={<OzelDosyaRoute><CaseFiles ozelMode /></OzelDosyaRoute>} />
                        <Route path="/ozel-dosyalar/:id" element={<OzelDosyaRoute><CaseFileDetail ozelMode /></OzelDosyaRoute>} />
                        <Route path="/archive-requests" element={<ArchiveRequests />} />
                        <Route path="/archived-files" element={<ArchivedFiles />} />
                        <Route path="/users" element={<Users />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/calendar" element={<Calendar />} />
                        <Route path="/tasks" element={<Tasks />} />
                        <Route path="/tasks/:id" element={<Tasks />} />
                        <Route path="/referanslar" element={<Referanslar />} />
                        <Route path="/matbu-dilekceler" element={<MatbuDilekceler />} />
                        <Route path="/referans-yonetim" element={<ReferansYonetim />} />
                        <Route path="/referans/:id/case-files" element={<ReferansCaseFiles />} />
                        <Route path="/masraflar" element={<Masraflar />} />
                        <Route path="/masraf-talepleri" element={<MasrafTalepleri />} />
                        <Route
                          path="/yasal-faiz-hesap"
                          element={
                            <RouteErrorBoundary>
                              <YasalFaizHesap />
                            </RouteErrorBoundary>
                          }
                        />
                        <Route
                          path="/tazminat-hesap"
                          element={
                            <RouteErrorBoundary>
                              <TazminatHesap />
                            </RouteErrorBoundary>
                          }
                        />
                        <Route path="/job-requests" element={<JobRequests />} />
                        <Route path="/job-list" element={<JobList />} />
                        <Route path="/kasa" element={<KasaRoute><Kasa /></KasaRoute>} />
                        <Route path="/kisisel-giderler" element={<RouteErrorBoundary><OzelDosyaRoute><KisiselGiderlerSayfasi /></OzelDosyaRoute></RouteErrorBoundary>} />
                        <Route path="/ayarlar" element={<RouteErrorBoundary><OzelDosyaRoute><Ayarlar /></OzelDosyaRoute></RouteErrorBoundary>} />
                        <Route path="/gelen-postalar" element={<RouteErrorBoundary><OzelDosyaRoute><GelenPostalar /></OzelDosyaRoute></RouteErrorBoundary>} />
                        {/* Eski adres yer imine alinmis olabilir. */}
                        <Route path="/gelen-yazismalar" element={<Navigate to="/gelen-postalar" replace />} />
                        <Route path="/vekaletler" element={<Vekaletler />} />
                        <Route path="/muvekkiller" element={<RouteErrorBoundary><Muvekkiller /></RouteErrorBoundary>} />
                        <Route path="/logs/user-login" element={<UserLoginLogs />} />
                        <Route path="/logs/case-file" element={<CaseFileLogs />} />
                        <Route path="/logs/masraf" element={<MasrafLogs />} />
                        <Route path="/logs/muvekkil" element={<RouteErrorBoundary><OzelDosyaRoute><MuvekkilLoglari /></OzelDosyaRoute></RouteErrorBoundary>} />
                        <Route path="/makbuz-hesapla" element={<MakbuzHesapla />} />
                        <Route path="/vekalet-ucreti-hesapla" element={<VekaletUcretiHesapla />} />
                        <Route path="/iscilik-alacaklari-hesap" element={<IscilikAlacaklariHesap />} />
                        {/* Onay bekleyen yenilik: yalnizca onizleme derlemesinde */}
                        <Route path="/arac-hasar-hesap" element={<AracHasarHesapSayfasi />} />
                        <Route path="/makbuzlar" element={<Makbuzlar />} />
                        <Route path="/ibraname-muvafakatname" element={<IbranameMuvafakatnameler />} />
                        <Route path="/" element={<Navigate to="/dashboard" />} />
                      </Routes>
                    </AppContentWrapper>
                    {YENI_TASARIM ? <CamDock /> : <BottomNav />}
                    <BackgroundUploadPanel />
                    <KalpYagmuru />
                  </>
                </PrivateRoute>
              }
            />
          </Routes>
        </div>
      </Router>
      </SidebarProvider>
    </AuthProvider>
  );
}

export default App;